import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { json, useParams } from "react-router-dom";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Tooltip, Box } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useUpdateUserOrgStatusMutation } from "state/rtkQuery/user/userMutations";
import { useGetUsersByOrgAndStatusQuery } from "state/rtkQuery/user/userQueries";
import { EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_APPROVER_VIEW } from "utils/constants";
import { generateRandomColor } from "utils/generateRandomColor";
import { useDebounce } from "utils/useDebounce";

import AvatarRandomColor from "../ReusableDataGrid/AvatarRandomColor";
import ReusableActions from "../ReusableDataGrid/ReusableActions";
import ReusableDataGrid from "../ReusableDataGrid/ReusableDataGrid";
import { DEFAULT_PAGE_SIZE } from "components/shared/ExpandableGrid/PageSizeSelect";
import { FilterChips, FilterPanel } from "components/shared/FilterPanel/FilterPanel";
import { useFilterPanel } from "components/shared/FilterPanel/useFilterPanel";
import { useTabSortFilter } from "../hooks/useTabSortFilter";

const RemoveRequestedTab = ({ orgName }) => {
  const [columns, setColumns] = useState([]);
  const [updatedRows, setUpdatedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const debouncedQuery = useDebounce(searchInput, 400);
  const { orgId } = useParams();

  const { data: platformData, isFetching: isPlatformDataLoading } =
    useGetPlatformsQuery();

  // Filter options for this tab
  // The filters offered on this tab — all switched-on filters apply together (the API ANDs them).
  const filterFields = useMemo(
    () => [
      { key: "email", label: "Email", type: "text" },
      { key: "first_name", label: "First Name", type: "text" },
      { key: "last_name", label: "Last Name", type: "text" },
      { key: "changed_date", label: "Date of Request", type: "date" },
      { key: "changed_by", label: "Remove Requested By", type: "text" },
    ],
    []
  );
  const { filters, setFilters, query: filterQuery } = useFilterPanel(filterFields, () => setCurrentPage(1));

  // Use the sorting and filtering hook
  const {
    sortBy,
    sortOrder,
    nameSortMenuAnchor,
    handleNameSortMenuOpen,
    renderNameSortMenu,
    renderNameSortIcon,
    renderSortIcon,
  } = useTabSortFilter([], {}, setCurrentPage);

  const {
    data: removeRequestedUserListData,
    isFetching: isRemoveRequestedUserListDataLoading,
  } = useGetUsersByOrgAndStatusQuery(
    {
      orgId: orgId,
      status: "Remove-Requested",
      platformId: platformData?._id,
      page: currentPage,
      perPage,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder || undefined,
      extraFilters: filterQuery,
    },
    { skip: !platformData?._id, refetchOnMountOrArgChange: true }
  );

  // Set datagrid rows
  // Combines both joined and remove requested users into a single list
  useEffect(() => {
    if (removeRequestedUserListData) {
      setUpdatedRows(removeRequestedUserListData?.users);
    }
  }, [removeRequestedUserListData]);

  // Logged in user's email
  const userEmail = useSelector((state) => state.auth.userAccount?.username);

  // Set columns
  useEffect(() => {
    if (updatedRows) {
      setColumns([
        {
          field: "name",
          headerName: `Showing ${(currentPage - 1) * perPage + 1} - ${(currentPage - 1) * perPage + updatedRows?.length
            } of ${removeRequestedUserListData?.total || 0} Users`,
          headerClassName: "history-tab-totalCount-header-approver-view",
          headerAlign: "left",
          align: "left",
          flex: 2,
          minWidth: 350,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className="history-tab-totalCount-header-approver-view"
                style={{ 
                  overflow: "hidden", 
                  textOverflow: "ellipsis", 
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0
                }}
              >
                {`Showing ${(currentPage - 1) * perPage + 1} - ${(currentPage - 1) * perPage + updatedRows?.length} of ${removeRequestedUserListData?.total || 0} Users`}
              </span>
              {renderNameSortIcon()}
              {renderNameSortMenu(setCurrentPage)}
            </Box>
          ),
          renderCell: (params) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                paddingLeft: "30px",
              }}
            >
              <AvatarRandomColor
                firstName={params.row.firstName}
                lastName={params.row.lastName}
                color={params.row.color}
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginLeft: "20px",
                }}
              >
                <Tooltip
                  title={
                    <span style={{ fontSize: "12px" }}>{params.row.name}</span>
                  }
                  placement="top-start"
                  arrow
                >
                  <span
                    className="table-history-userEmailJoined-cell"
                    style={{ fontWeight: "600", fontSize: "14px" }}
                  >
                    {params.row.name}
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    <span style={{ fontSize: "12px" }}>{params.row.email}</span>
                  }
                  arrow
                  placement="bottom-start"
                >
                  {params.row.email.length >
                    EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_APPROVER_VIEW ? (
                    <div>
                      <span
                        className="table-cell-trucate-reason"
                        style={{ paddingLeft: "0", fontSize: "13px" }}
                      >
                        {params.row.email?.slice(
                          0,
                          EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_APPROVER_VIEW
                        ) + "..."}
                      </span>{" "}
                    </div>
                  ) : (
                    <span style={{ fontSize: "13px" }}>{params.row.email}</span>
                  )}
                </Tooltip>
              </div>
            </div>
          ),
        },
        {
          field: "JoinedDate",
          headerName: "Date of Request",
          headerClassName: "date-joined-table-header",
          headerAlign: "center",
          align: "center",
          flex: 1.3,
          minWidth: 240,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className="date-joined-table-header"
                style={{ 
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "0 1 auto"
                }}
              >
                Date of Request
              </span>
              {renderSortIcon("changed_date", "Date of Request")}
            </Box>
          ),
          valueGetter: (params) => {
            // Return the date as a Date object for proper sorting
            return new Date(params.row.date);
          },
          renderCell: (params) => (
            <span className="history-tab-joinedDate-cell ">
              {params.row.date}
            </span>
          ),
        },
        {
          field: "removedBy",
          headerName: "Remove Requested By",
          headerClassName: "orgName-column-header",
          headerAlign: "center",
          align: "center",
          flex: 2,
          minWidth: 350,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className="orgName-column-header"
                style={{ 
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "0 1 auto"
                }}
              >
                Remove Requested By
              </span>
              {renderSortIcon("changed_by", "Remove Requested By")}
            </Box>
          ),
          renderCell: (params) => (
            <Tooltip
              title={<p style={{ fontSize: "12px" }}>{params.row.changedBy}</p>}
            >
              <span className="table-cell-history">{params.row.changedBy}</span>
            </Tooltip>
          ),
        },
      ]);
    }
  }, [updatedRows, currentPage, perPage, removeRequestedUserListData?.total, sortBy, sortOrder, nameSortMenuAnchor]);

  return (
    <div>
      <ReusableDataGrid
        columns={columns}
        rows={updatedRows}
        orgName={orgName}
        viewType={"Removals Requested History"}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        recordsPerPage={perPage}
        onRecordsPerPageChange={(size) => {
          setPerPage(size);
          setCurrentPage(1);
        }}
        checkboxSelection={false}
        totalRecords={removeRequestedUserListData?.total || 0}
        isLoading={
          isRemoveRequestedUserListDataLoading || isPlatformDataLoading
        }
        customSearchControls={<FilterPanel fields={filterFields} filters={filters} onFiltersChange={setFilters} height={31} />}
        customSearchInput={<></>}
        filterChips={<FilterChips fields={filterFields} filters={filters} onFiltersChange={setFilters} />}
        tabDescription={
          "Users in Removals Requested have had a request made to have their access revoked from the organization and are in the Active Directory sync process"
        }
      />
    </div>
  );
};

export default RemoveRequestedTab;
